import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Fab,
  Tooltip,
  keyframes,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import usePopup from "../../hooks/usePopup";
import VoiceAssistantMicAnimation from "./VoiceAssistantMicAnimation";
import {
  VOICE_COMMAND_EVENT,
  type VoiceCommandDetail,
  useVoiceAssistant,
} from "../../context/VoiceAssistant/VoiceAssistantContext";
import { getItem, setItem } from "../../utilis/methods/storage/storage";

const FAB_BOTTOM = 24;
const FAB_RIGHT = 24;
const FAB_SIZE = 56;
const FAB_POSITION_STORAGE_KEY = "voiceAssistantFabPosition";
const DRAG_THRESHOLD_PX = 5;

/** Reserve space so fixed FAB does not cover drawer controls (e.g. EMR send button). */
export const VOICE_ASSISTANT_FAB_RESERVE_BOTTOM = FAB_BOTTOM + FAB_SIZE + 16;
export const VOICE_ASSISTANT_FAB_RESERVE_RIGHT = FAB_RIGHT + FAB_SIZE + 12;

type FabPosition = { x: number; y: number };

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.55); }
  70% { box-shadow: 0 0 0 14px rgba(25, 118, 210, 0); }
  100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

const FAB_WRAPPER_SIZE = 100;

const VOICE_SNACK_ORIGIN = {
  vertical: "bottom" as const,
  horizontal: "right" as const,
};

function getDefaultFabPosition(): FabPosition {
  return {
    x: Math.max(8, window.innerWidth - FAB_RIGHT - FAB_SIZE),
    y: Math.max(8, window.innerHeight - FAB_BOTTOM - FAB_SIZE),
  };
}

function clampFabPosition(position: FabPosition): FabPosition {
  const margin = 8;
  const maxX = window.innerWidth - FAB_SIZE - margin;
  const maxY = window.innerHeight - FAB_SIZE - margin;
  return {
    x: Math.min(Math.max(margin, position.x), maxX),
    y: Math.min(Math.max(margin, position.y), maxY),
  };
}

function readStoredFabPosition(): FabPosition | null {
  const stored = getItem(FAB_POSITION_STORAGE_KEY) as FabPosition | null;
  if (
    stored &&
    typeof stored.x === "number" &&
    typeof stored.y === "number" &&
    Number.isFinite(stored.x) &&
    Number.isFinite(stored.y)
  ) {
    return clampFabPosition(stored);
  }
  return null;
}

export default function GlobalVoiceAssistantFab() {
  const theme = useTheme();
  const { showSnackbar } = usePopup();
  const { isVoiceEnabled, phase, lastError, toggleVoiceEnabled } =
    useVoiceAssistant();

  const [position, setPosition] = useState<FabPosition>(() => {
    return readStoredFabPosition() ?? getDefaultFabPosition();
  });
  const [isDragging, setIsDragging] = useState(false);

  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const dragStateRef = useRef({
    active: false,
    moved: false,
    pointerId: -1,
    offsetX: 0,
    offsetY: 0,
    startX: 0,
    startY: 0,
  });

  const persistPosition = useCallback((next: FabPosition) => {
    setItem(FAB_POSITION_STORAGE_KEY, next);
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;

      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);

      dragStateRef.current = {
        active: true,
        moved: false,
        pointerId: event.pointerId,
        offsetX: event.clientX - position.x,
        offsetY: event.clientY - position.y,
        startX: event.clientX,
        startY: event.clientY,
      };
    },
    [position.x, position.y],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragStateRef.current;
      if (!drag.active || drag.pointerId !== event.pointerId) return;

      const deltaX = Math.abs(event.clientX - drag.startX);
      const deltaY = Math.abs(event.clientY - drag.startY);
      if (deltaX > DRAG_THRESHOLD_PX || deltaY > DRAG_THRESHOLD_PX) {
        drag.moved = true;
        setIsDragging(true);
      }

      if (!drag.moved) return;

      setPosition(
        clampFabPosition({
          x: event.clientX - drag.offsetX,
          y: event.clientY - drag.offsetY,
        }),
      );
    },
    [],
  );

  const finishPointerInteraction = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragStateRef.current;
      if (!drag.active || drag.pointerId !== event.pointerId) return;

      drag.active = false;
      setIsDragging(false);

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      setPosition((current) => {
        const clamped = clampFabPosition(current);
        persistPosition(clamped);
        return clamped;
      });

      if (!drag.moved) {
        toggleVoiceEnabled();
      }
    },
    [persistPosition, toggleVoiceEnabled],
  );

  useEffect(() => {
    const onVoiceCommand = (event: Event) => {
      const { text } = (event as CustomEvent<VoiceCommandDetail>).detail;
      if (!text) return;

      showSnackbarRef.current({
        open: true,
        message: `Heard command: ${text}`,
        variant: "info",
        duration: 4000,
        origin: VOICE_SNACK_ORIGIN,
        direction: "up",
      });
    };

    window.addEventListener(VOICE_COMMAND_EVENT, onVoiceCommand);
    return () => window.removeEventListener(VOICE_COMMAND_EVENT, onVoiceCommand);
  }, []);

  useEffect(() => {
    if (!isVoiceEnabled) {
      showSnackbarRef.current({
        open: false,
        message: "",
        variant: undefined,
      });
    }
  }, [isVoiceEnabled]);

  useEffect(() => {
    const onResize = () => {
      setPosition((current) => {
        const clamped = clampFabPosition(current);
        persistPosition(clamped);
        return clamped;
      });
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [persistPosition]);

  const tooltipTitle = useMemo(() => {
    if (!isVoiceEnabled && lastError) {
      return lastError;
    }
    if (!isVoiceEnabled) {
      return 'Voice assistant off — click to enable (say "wake up" to start, "sleep" to stop). Drag to move.';
    }
    if (phase === "wake_listening") {
      return 'Listening for "wake up"…';
    }
    if (phase === "command_listening") {
      return 'Command mode — focus a text field and speak to fill it, or say commands; say "sleep" to stop';
    }
    if (phase === "error") {
      return lastError || "Voice error — click to turn off";
    }
    return "Voice assistant — drag to move";
  }, [isVoiceEnabled, phase, lastError]);

  const showCommandAnimation = isVoiceEnabled && phase === "command_listening";

  const fabSx = useMemo(() => {
    if (!isVoiceEnabled || phase === "off") {
      return {
        bgcolor: lastError ? "warning.light" : "grey.400",
        color: lastError ? "warning.contrastText" : "grey.900",
        "&:hover": {
          bgcolor: lastError ? "warning.main" : "grey.500",
        },
      };
    }
    if (phase === "error") {
      return {
        bgcolor: "error.main",
        color: "error.contrastText",
        animation: `${pulse} 1.6s ease-out infinite`,
      };
    }
    if (phase === "command_listening") {
      return {
        width: FAB_WRAPPER_SIZE,
        height: FAB_WRAPPER_SIZE,
        minHeight: FAB_WRAPPER_SIZE,
        bgcolor: "transparent",
        color: "inherit",
        boxShadow: "none",
        overflow: "visible",
        "&:hover": {
          bgcolor: "transparent",
          boxShadow: "none",
        },
      };
    }
    return {
      bgcolor: "primary.main",
      color: "primary.contrastText",
      animation: `${pulse} 2s ease-out infinite`,
    };
  }, [isVoiceEnabled, phase, lastError]);

  const animationOffset = showCommandAnimation
    ? (FAB_WRAPPER_SIZE - FAB_SIZE) / 2
    : 0;

  return (
    <Box
      sx={{
        position: "fixed",
        left: position.x - animationOffset,
        top: position.y - animationOffset,
        zIndex: theme.zIndex.snackbar + 2,
        touchAction: "none",
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishPointerInteraction}
      onPointerCancel={finishPointerInteraction}
    >
      <Tooltip
        title={tooltipTitle}
        placement="left"
        arrow
        disableHoverListener={isDragging}
        disableFocusListener={isDragging}
      >
        <Fab
          aria-label={
            isVoiceEnabled ? "Disable voice assistant" : "Enable voice assistant"
          }
          size="medium"
          sx={fabSx}
        >
          {showCommandAnimation ? (
            <VoiceAssistantMicAnimation size={42} />
          ) : (
            <AutoAwesomeIcon />
          )}
        </Fab>
      </Tooltip>
    </Box>
  );
}
